import LegalLayout, { LegalSection } from "@/pages/LegalLayout";
import Seo from "@/components/Seo";

export default function Terms() {
  return (
    <LegalLayout title="Terms of Service" updated="June 2026" testId="terms-page">
      <Seo
        title="Terms of Service — 5ads.ai"
        description="The terms governing your use of 5ads.ai and its AI-powered ad manager applications, social.5ads.ai and search.5ads.ai."
        url="https://5ads.ai/terms"
      />
      <LegalSection heading="1. Agreement">
        <p>
          These Terms of Service ("Terms") govern your use of 5ads.ai and its applications, including
          social.5ads.ai and the upcoming search.5ads.ai (together, the "Services"). By creating an account
          or using the Services you agree to these Terms.
        </p>
      </LegalSection>

      <LegalSection heading="2. The Services">
        <p>
          5ads.ai provides AI-powered advertising management tools. Our AI drafts creatives, ad copy, budget
          changes and optimizations for your review. Actions are executed on your connected ad accounts only
          after your explicit approval, within guardrails you configure.
        </p>
      </LegalSection>

      <LegalSection heading="3. Your responsibilities">
        <ul className="list-disc pl-5 space-y-2">
          <li>You must own or have authorization to manage the ad accounts you connect.</li>
          <li>You are responsible for reviewing and approving AI-proposed actions before they execute.</li>
          <li>You are responsible for all ad spend incurred on your connected accounts and for complying with the advertising policies of Meta, Google, and other platforms.</li>
          <li>You will not use the Services for unlawful, misleading, or discriminatory advertising.</li>
          <li>You are responsible for keeping your account credentials secure.</li>
        </ul>
      </LegalSection>

      <LegalSection heading="4. AI-generated content">
        <p>
          Creatives, copy and recommendations generated by our AI are provided as drafts for your review.
          You are solely responsible for the content of ads you approve and publish. We do not guarantee any
          specific advertising performance, cost-per-lead, or return on ad spend.
        </p>
      </LegalSection>

      <LegalSection heading="5. Plans, credits and billing">
        <p>
          Paid plans are billed in advance on a monthly basis. AI credits included in each plan reset monthly
          and do not roll over. You may cancel at any time; access continues until the end of the current
          billing period. Fees are non-refundable except where required by law.
        </p>
      </LegalSection>

      <LegalSection heading="6. Third-party platforms">
        <p>
          The Services depend on third-party APIs (such as the Meta Marketing API and Google Ads API). We are
          not responsible for changes, outages, or restrictions imposed by those platforms. Your use of those
          platforms remains subject to their own terms.
        </p>
      </LegalSection>

      <LegalSection heading="7. Intellectual property">
        <p>
          We retain all rights to the Services, software and branding. You retain all rights to your brand
          assets, ad account data, and the ad content you approve and publish. You grant us a limited license
          to process your data and assets solely to provide the Services.
        </p>
      </LegalSection>

      <LegalSection heading="8. Termination">
        <p>
          You may stop using the Services and delete your account at any time. We may suspend or terminate
          accounts that violate these Terms or abuse the Services. Upon termination, your data is deleted per
          our <a href="/privacy" className="text-[#FF4D00] underline underline-offset-2">Privacy Policy</a>.
        </p>
      </LegalSection>

      <LegalSection heading="9. Disclaimers and limitation of liability">
        <p>
          The Services are provided "as is" without warranties of any kind. To the maximum extent permitted
          by law, 5ads.ai shall not be liable for indirect, incidental, or consequential damages, including
          lost profits or ad spend outcomes. Our total liability for any claim is limited to the amounts you
          paid us in the 12 months preceding the claim.
        </p>
      </LegalSection>

      <LegalSection heading="10. Changes to these Terms">
        <p>
          We may update these Terms from time to time. Continued use of the Services after changes take
          effect constitutes acceptance of the revised Terms.
        </p>
      </LegalSection>

      <LegalSection heading="11. Contact">
        <p>
          Questions about these Terms? Email <a href="mailto:legal@5ads.ai" className="text-[#FF4D00] underline underline-offset-2">legal@5ads.ai</a> or
          use the contact form on <a href="/#contact" className="text-[#FF4D00] underline underline-offset-2">5ads.ai</a>.
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
